import React, { useMemo } from 'react';
import { EntityType } from '@/components/entities/entityType';
import { useApiContext } from '@/contexts/apiContext';
import { ExecutionStep, PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppsIcon from '@mui/icons-material/Apps';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import { RightPanelContext } from '../entityContextPanel/entityContextPanel';
import { ChatMessage, DetectedEntityInfo, QuickAction } from './types';

interface UseQuickActionsParams {
  rightPanelContext?: RightPanelContext;
  pipelineExecutionState?: PipelineExecutionState;
  selectedStep?: ExecutionStep | null;
  suppressEvents?: boolean;
  onExecuteStep?: (
    step: ExecutionStep,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void>;
  onEntityDetected?: (entityInfo: DetectedEntityInfo) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  setError: (err: string | null) => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  handleSendMessage: (prompt: string) => void;
  conversationIdRef?: React.MutableRefObject<string | null>;
  setConvId?: (id: string | null) => void;
  onConversationSaved?: (newConvId?: string) => void;
}

export const useQuickActions = ({
  rightPanelContext,
  pipelineExecutionState,
  selectedStep,
  suppressEvents,
  onExecuteStep,
  onEntityDetected,
  isProcessing,
  setIsProcessing,
  setError,
  setMessages,
  handleSendMessage,
  conversationIdRef,
  setConvId,
  onConversationSaved,
}: UseQuickActionsParams) => {
  const apiContext = useApiContext();


  const quickActions: QuickAction[] = useMemo(() => {
    if (!rightPanelContext || rightPanelContext.type === 'empty') {
      return [
        {
          label: 'Discover Components',
          prompt: 'What data sources and agents do I have available?',
          icon: <SearchIcon sx={{ fontSize: 13 }} />,
          directAction: async (assistantMsgId: string) => {
            const [dsInstances, agentInstances, appInstances] = await Promise.all([
              apiContext.getDataSourceInstances(false, false),
              apiContext.getAgentInstances(false, false),
              apiContext.getUserAppInstances(false, false),
            ]);
            const allInstances = [
              ...(dsInstances || []).map((d) => ({ ...d, entityType: EntityType.dataSourceInstance })),
              ...(agentInstances || []).map((d) => ({ ...d, entityType: EntityType.agentInstance })),
              ...(appInstances || []).map((d) => ({ ...d, entityType: EntityType.userAppInstance })),
            ];
            onEntityDetected?.({
              type: 'entity_list',
              entityType: EntityType.pipelineInstance,
              title: 'Available Components',
              items: allInstances,
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? {
                      ...msg,
                      content: `Found **${allInstances.length}** component instance(s) (${dsInstances?.length || 0} Data Sources, ${agentInstances?.length || 0} Agents, ${appInstances?.length || 0} User Apps). You can explore and inspect them in the workspace panel on the right.`,
                      toolCalls: [
                        {
                          id: 'direct_discovery',
                          name: 'list_components',
                          result: 'Completed',
                        },
                      ],
                      isStreaming: false,
                      status: undefined,
                    }
                  : msg,
              ),
            );
          },
        },
        {
          label: 'List Pipelines',
          prompt: 'List all my pipelines',
          icon: <AccountTreeIcon sx={{ fontSize: 13 }} />,
          directAction: async (assistantMsgId: string) => {
            const pipelines = await apiContext.getPipelineInstances(undefined, undefined, false);
            onEntityDetected?.({
              type: 'entity_list',
              entityType: EntityType.pipelineInstance,
              title: 'Pipelines',
              items: pipelines,
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? {
                      ...msg,
                      content: `I retrieved **${pipelines?.length || 0}** pipeline${(pipelines?.length || 0) === 1 ? '' : 's'} in your workspace. You can select and inspect them in the workspace panel on the right.`,
                      toolCalls: [
                        {
                          id: 'direct_list_pipelines',
                          name: 'list_pipelines',
                          result: JSON.stringify(pipelines),
                        },
                      ],
                      isStreaming: false,
                      status: undefined,
                    }
                  : msg,
              ),
            );
          },
        },
        {
          label: 'Create Pipeline',
          prompt: 'Create a new pipeline for document processing',
          icon: <AutoAwesomeIcon sx={{ fontSize: 13 }} />,
        },
        {
          label: 'Search Papers',
          prompt: 'Search arXiv for recent AI research papers',
          icon: <AutoAwesomeIcon sx={{ fontSize: 13 }} />,
        },
      ];
    }

    // Pipeline Context
    if (rightPanelContext.type === 'pipeline' || rightPanelContext.pipelineId) {
      const pName = rightPanelContext.pipelineName || rightPanelContext.pipelineId || 'pipeline';
      const pId = rightPanelContext.pipelineId || pipelineExecutionState?.id || '';

      const actions: QuickAction[] = [
        {
          label: 'Reingest all',
          prompt: `Reingest all steps of pipeline "${pName}"`,
          icon: <PlayArrowIcon sx={{ fontSize: 13, color: '#16a34a' }} />,
          directAction: async (assistantMsgId: string) => {
            if (!pId) throw new Error('No active pipeline found');
            const plan = await apiContext.getPipelineExecutionPlan(pId);
            const firstStep = plan?.steps?.[0];
            if (firstStep) {
              if (onExecuteStep) {
                await onExecuteStep(firstStep, { reingest: true });
              } else {
                await apiContext.executePipelineStep(pId, {
                  action: 'execute',
                  stepId: firstStep.id,
                  entityType: firstStep.entityType,
                  entityDefId: firstStep.entityDefId,
                  entityId: firstStep.entityId,
                  reingest: true,
                });
              }
            }
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? {
                      ...msg,
                      content: `Started pipeline execution for **"${plan?.pipelineName || pName}"**. Live progress is updating in the workspace panel.`,
                      toolCalls: [
                        {
                          id: 'direct_run_pipeline',
                          name: 'execute_pipeline_step',
                          result: 'Completed',
                        },
                      ],
                      isStreaming: false,
                      status: undefined,
                    }
                  : msg,
              ),
            );
          },
        },
        {
          label: 'Status',
          prompt: `What is the current execution plan and status of pipeline "${pName}"?`,
          icon: <HistoryIcon sx={{ fontSize: 13, color: '#2563eb' }} />,
          directAction: async (assistantMsgId: string) => {
            if (!pId) throw new Error('No active pipeline found');
            const plan = await apiContext.getPipelineExecutionPlan(pId);
            const completedSteps = plan?.steps?.filter((s) => s.status === 'completed').length || 0;
            const totalSteps = plan?.steps?.length || 0;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? {
                      ...msg,
                      content: `Pipeline **"${plan?.pipelineName || pName}"** status is **${(plan?.status || 'pending').toUpperCase()}** (${completedSteps}/${totalSteps} steps completed). Detailed step execution is visible in the workspace panel.`,
                      toolCalls: [
                        {
                          id: 'direct_status',
                          name: 'get_execution_plan',
                          result: JSON.stringify(plan),
                        },
                      ],
                      isStreaming: false,
                      status: undefined,
                    }
                  : msg,
              ),
            );
          },
        },
        {
          label: 'Summarize',
          prompt: `Summarize the processed results and output items of pipeline "${pName}"`,
          icon: <AutoAwesomeIcon sx={{ fontSize: 13, color: '#8b5cf6' }} />,
        },
      ];

      if (rightPanelContext.selectedStepId || selectedStep) {
        const stepToRun =
          selectedStep ||
          pipelineExecutionState?.steps?.find((s) => s.id === rightPanelContext.selectedStepId) ||
          pipelineExecutionState?.steps?.[0];

        const isDataSource =
          stepToRun?.entityType === EntityType.dataSourceInstance ||
          stepToRun?.entityType === ('dataSourceInstance' as any);

        const isUserApp =
          stepToRun?.entityType === EntityType.userAppInstance ||
          stepToRun?.entityType === ('userAppInstance' as any);

        const stepLabel = isDataSource
          ? suppressEvents ? 'Scan data source only' : 'Scan data source'
          : isUserApp
          ? suppressEvents ? 'Run all source agents only' : 'Run all source agents'
          : suppressEvents ? 'Run agent only' : 'Run agent';

        const stepPrompt = isDataSource
          ? suppressEvents
            ? `Scan data source "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" only (suppress subsequent events)`
            : `Scan data source "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" of pipeline "${pName}"`
          : isUserApp
          ? suppressEvents
            ? `Run all source agents of user app "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" of pipeline "${pName}" only (suppress subsequent events)`
            : `Run all source agents of user app "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" of pipeline "${pName}"`
          : suppressEvents
            ? `Run agent "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" only (suppress subsequent events)`
            : `Run agent "${rightPanelContext.selectedStepId || selectedStep?.name || stepToRun?.name}" of pipeline "${pName}"`;

        actions.unshift({
          label: stepLabel,
          prompt: stepPrompt,
          icon: <PlayArrowIcon sx={{ fontSize: 13, color: '#16a34a' }} />,
          directAction: async (assistantMsgId: string) => {
            if (!stepToRun) throw new Error('No step selected to execute');

            if (onExecuteStep) {
              await onExecuteStep(stepToRun, { suppressEvents, reingest: isDataSource });
            } else if (pId) {
              await apiContext.executePipelineStep(pId, {
                action: 'execute',
                stepId: stepToRun.id,
                entityType: stepToRun.entityType,
                entityDefId: stepToRun.entityDefId,
                entityId: stepToRun.entityId,
                reingest: isDataSource ? true : undefined,
                suppressEvents,
              });
            }

            const actionDesc = isDataSource
              ? 'data source'
              : isUserApp
              ? 'all source agents of user app'
              : 'agent';

            const toolName = isUserApp ? 'run_all_source_agents' : 'execute_pipeline_step';

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? {
                      ...msg,
                      content: suppressEvents
                        ? `Triggered isolated execution for ${actionDesc} **"${stepToRun.name || stepToRun.id}"** (subsequent events suppressed). Live progress and results are updating in the workspace panel.`
                        : `Triggered execution for ${actionDesc} **"${stepToRun.name || stepToRun.id}"**. Live progress and results are updating in the workspace panel.`,
                      toolCalls: [
                        {
                          id: 'direct_exec_step',
                          name: toolName,
                          arguments: JSON.stringify({ stepId: stepToRun.id, name: stepToRun.name, suppressEvents }),
                          result: 'Completed',
                        },
                      ],
                      isStreaming: false,
                      status: undefined,
                    }
                  : msg,
              ),
            );
          },
        });
      }

      return actions;
    }

    // Single Entity Context
    if (rightPanelContext.type === 'single' || rightPanelContext.entityType) {
      const eName = rightPanelContext.entityName || rightPanelContext.entityId || 'entity';
      const eType = rightPanelContext.entityType;

      if (eType === EntityType.dataSourceInstance || eType === ('dataSourceInstance' as any)) {
        return [
          {
            label: 'Sync Data Source',
            prompt: `Sync and ingest items from data source "${eName}"`,
            icon: <SyncIcon sx={{ fontSize: 13, color: '#0284c7' }} />,
          },
          {
            label: 'List Ingested Items',
            prompt: `What items have been ingested in data source "${eName}"?`,
            icon: <StorageIcon sx={{ fontSize: 13, color: '#0284c7' }} />,
            directAction: async (assistantMsgId: string) => {
              const defId = rightPanelContext.entityDefId || (rightPanelContext as any).defId || '';
              const instId = rightPanelContext.entityId || '';
              const items = await apiContext.getDataSourceInstanceItems(defId, instId);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: `Data Source **"${eName}"** currently has **${items?.length || 0}** ingested item(s).`,
                        toolCalls: [
                          {
                            id: 'direct_ds_items',
                            name: 'get_data_source_status',
                            result: `Found ${items?.length || 0} items`,
                          },
                        ],
                        isStreaming: false,
                        status: undefined,
                      }
                    : msg,
                ),
              );
            },
          },
          {
            label: 'Check Status',
            prompt: `Get processing status and item count for data source "${eName}"`,
            icon: <HistoryIcon sx={{ fontSize: 13 }} />,
            directAction: async (assistantMsgId: string) => {
              const defId = rightPanelContext.entityDefId || (rightPanelContext as any).defId || '';
              const instId = rightPanelContext.entityId || '';
              const status = await apiContext.getDataSourceInstanceProcessingStatus(defId, instId);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: `Data Source **"${eName}"** processing status: **${status?.status || 'idle'}** (total items: ${status?.totalItems || 0}).`,
                        toolCalls: [
                          {
                            id: 'direct_ds_status',
                            name: 'get_data_source_status',
                            result: JSON.stringify(status),
                          },
                        ],
                        isStreaming: false,
                        status: undefined,
                      }
                    : msg,
                ),
              );
            },
          },
        ];
      }

      if (eType === EntityType.agentInstance || eType === ('agentInstance' as any)) {
        return [
          {
            label: 'Run Agent',
            prompt: `Run agent "${eName}"`,
            icon: <PlayArrowIcon sx={{ fontSize: 13, color: '#16a34a' }} />,
          },
          {
            label: 'Get Agent Results',
            prompt: `Show processed outputs and results for agent "${eName}"`,
            icon: <SmartToyIcon sx={{ fontSize: 13, color: '#7c3aed' }} />,
            directAction: async (assistantMsgId: string) => {
              const defId = rightPanelContext.entityDefId || (rightPanelContext as any).defId || '';
              const instId = rightPanelContext.entityId || '';
              const statuses = await apiContext.getAgentInstanceItemProcessingStatuses(defId, instId);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: `Agent **"${eName}"** has processed **${statuses?.length || 0}** item(s).`,
                        toolCalls: [
                          {
                            id: 'direct_agent_results',
                            name: 'get_agent_results',
                            result: `Found ${statuses?.length || 0} items`,
                          },
                        ],
                        isStreaming: false,
                        status: undefined,
                      }
                    : msg,
                ),
              );
            },
          },
          {
            label: 'Check Config',
            prompt: `What is the configuration and model for agent "${eName}"?`,
            icon: <SearchIcon sx={{ fontSize: 13 }} />,
          },
        ];
      }

      if (eType === EntityType.userAppInstance || eType === ('userAppInstance' as any)) {
        return [
          {
            label: 'Run all source agents',
            prompt: `Run all source agents connected to user app "${eName}"`,
            icon: <PlayArrowIcon sx={{ fontSize: 13, color: '#16a34a' }} />,
            directAction: async (assistantMsgId: string) => {
              const defId = rightPanelContext.entityDefId || (rightPanelContext as any).defId || '';
              const instId = rightPanelContext.entityId || '';
              let sourceAgents: any[] = [];
              if (defId && instId) {
                try {
                  const configured = await apiContext.getUserAppInstanceSourceEntities(defId, instId);
                  sourceAgents = (configured || []).filter(
                    (s: any) =>
                      s.entityType === EntityType.agentInstance ||
                      s.sourceEntityType === EntityType.agentInstance ||
                      Boolean(s.agentDefId),
                  );
                } catch (err) {
                  console.error('Failed to fetch user app source entities:', err);
                }
              }

              if (sourceAgents.length === 0) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMsgId
                      ? {
                          ...msg,
                          content: `User app **"${eName}"** does not have any connected source agents configured.`,
                          toolCalls: [
                            {
                              id: 'direct_run_source_agents',
                              name: 'run_all_source_agents',
                              result: 'No source agents found',
                            },
                          ],
                          isStreaming: false,
                          status: undefined,
                        }
                      : msg,
                  ),
                );
                return;
              }

              const agentNames = sourceAgents.map((a: any) => `"${a.name || a.id}"`).join(', ');
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: `Found **${sourceAgents.length}** source agent(s) (${agentNames}) configured for user app **"${eName}"**. Triggering execution...`,
                        toolCalls: [
                          {
                            id: 'direct_run_source_agents',
                            name: 'run_all_source_agents',
                            arguments: JSON.stringify({
                              userApp: eName,
                              sourceAgents: sourceAgents.map((a: any) => ({ id: a.id, name: a.name })),
                            }),
                            result: 'Completed',
                          },
                        ],
                        isStreaming: false,
                        status: undefined,
                      }
                    : msg,
                ),
              );
            },
          },
          {
            label: 'Inspect App',
            prompt: `Show details and configuration for user app "${eName}"`,
            icon: <AppsIcon sx={{ fontSize: 13, color: '#d97706' }} />,
          },
          {
            label: 'Connected Agents',
            prompt: `What source entities and agents are connected to user app "${eName}"?`,
            icon: <AccountTreeIcon sx={{ fontSize: 13 }} />,
          },
        ];
      }

      if (eType === EntityType.pipelineBlueprint || eType === ('pipelineBlueprint' as any)) {
        return [
          {
            label: 'Create from Blueprint',
            prompt: `Create a new pipeline from blueprint "${eName}"`,
            icon: <AutoAwesomeIcon sx={{ fontSize: 13, color: '#2563eb' }} />,
          },
          {
            label: 'Inspect Blueprint',
            prompt: `Show details and entities in blueprint "${eName}"`,
            icon: <SearchIcon sx={{ fontSize: 13 }} />,
          },
        ];
      }
    }

    // Entity List Context
    if (rightPanelContext.type === 'entity_list' || rightPanelContext.entityList) {
      const listTitle = rightPanelContext.entityList?.title || 'Components';
      const listType = rightPanelContext.entityList?.entityType;

      if (listType === EntityType.pipelineInstance || listType === ('pipelineInstance' as any)) {
        return [
          {
            label: 'Create New Pipeline',
            prompt: 'Create a new pipeline for document processing',
            icon: <AutoAwesomeIcon sx={{ fontSize: 13, color: '#2563eb' }} />,
          },
          {
            label: 'List Blueprints',
            prompt: 'List available pipeline blueprints',
            icon: <AccountTreeIcon sx={{ fontSize: 13 }} />,
            directAction: async (assistantMsgId: string) => {
              const blueprints = await apiContext.getPipelineBlueprints();
              onEntityDetected?.({
                type: 'entity_list',
                entityType: EntityType.pipelineBlueprint,
                title: 'Pipeline Blueprints',
                items: blueprints,
              });
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: `Found **${blueprints?.length || 0}** pipeline blueprint template(s) in your workspace.`,
                        toolCalls: [
                          {
                            id: 'direct_blueprints',
                            name: 'list_pipeline_blueprints',
                            result: 'Completed',
                          },
                        ],
                        isStreaming: false,
                        status: undefined,
                      }
                    : msg,
                ),
              );
            },
          },
        ];
      }

      if (listType === EntityType.dataSourceInstance || listType === ('dataSourceInstance' as any)) {
        return [
          {
            label: 'Create Data Source',
            prompt: 'Create a new data source',
            icon: <StorageIcon sx={{ fontSize: 13, color: '#0284c7' }} />,
          },
          {
            label: 'Check Statuses',
            prompt: 'Check the status of all data sources',
            icon: <SyncIcon sx={{ fontSize: 13 }} />,
          },
        ];
      }

      if (listType === EntityType.agentInstance || listType === ('agentInstance' as any)) {
        return [
          {
            label: 'List Agent Types',
            prompt: 'List all agent definitions and models available',
            icon: <SmartToyIcon sx={{ fontSize: 13, color: '#7c3aed' }} />,
          },
        ];
      }

      return [
        {
          label: `Inspect ${listTitle}`,
          prompt: `List all ${listTitle.toLowerCase()}`,
          icon: <SearchIcon sx={{ fontSize: 13 }} />,
        },
      ];
    }

    return [];
  }, [
    rightPanelContext,
    pipelineExecutionState,
    selectedStep,
    suppressEvents,
    onExecuteStep,
    apiContext,
    onEntityDetected,
    setMessages,
  ]);

  const [activeActionLabel, setActiveActionLabel] = React.useState<string | null>(null);

  const executeDirectAction = async (
    label: string,
    actionFn: (assistantMsgId: string) => Promise<void>,
  ) => {
    if (isProcessing) return;

    setActiveActionLabel(label);
    setIsProcessing(true);
    setError(null);

    const userMsgId = `user_${Date.now()}`;
    const assistantMsgId = `asst_${Date.now()}`;
    const timestamp = new Date();

    setMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        role: 'user',
        content: label,
        timestamp,
      },
      {
        id: assistantMsgId,
        role: 'system',
        content: '',
        timestamp,
        status: `Executing ${label}...`,
        isStreaming: true,
      },
    ]);

    let recordedResponse = '';
    let recordedToolCalls: any[] | undefined = undefined;
    try {
      await actionFn(assistantMsgId);
      recordedResponse = `Completed **${label}**. Live context is updated in the workspace panel.`;
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === assistantMsgId) {
            recordedResponse = msg.content || recordedResponse;
            recordedToolCalls = msg.toolCalls || [
              {
                id: `direct_${Date.now()}`,
                name: label.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                result: 'Completed',
              },
            ];
            return {
              ...msg,
              content: recordedResponse,
              toolCalls: recordedToolCalls,
              isStreaming: false,
              status: undefined,
            };
          }
          return msg;
        }),
      );

      // Persist quick action interaction to database
      try {
        const currentConvId = conversationIdRef?.current || 'new';
        const res = await apiContext.createAssistantInteraction(
          currentConvId,
          label,
          recordedResponse,
          recordedToolCalls,
        );
        if (res?.conversationId) {
          setConvId?.(res.conversationId);
          if (onConversationSaved) {
            onConversationSaved(res.conversationId);
          }
        }
      } catch (saveErr) {
        console.error('Failed to record quick action interaction:', saveErr);
      }
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.message || err?.message || 'Operation failed. Please try again.';
      recordedResponse = `Error executing **${label}**: ${errMsg}`;
      const errToolCalls = [
        {
          id: `direct_${Date.now()}`,
          name: label.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
          result: `Error: ${errMsg}`,
        },
      ];
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                content: recordedResponse,
                toolCalls: errToolCalls,
                isStreaming: false,
                status: undefined,
              }
            : msg,
        ),
      );

      // Persist error interaction to database as well
      try {
        const currentConvId = conversationIdRef?.current || 'new';
        const res = await apiContext.createAssistantInteraction(
          currentConvId,
          label,
          recordedResponse,
          errToolCalls,
        );
        if (res?.conversationId) {
          setConvId?.(res.conversationId);
          if (onConversationSaved) {
            onConversationSaved(res.conversationId);
          }
        }
      } catch (saveErr) {
        console.error('Failed to record quick action error interaction:', saveErr);
      }
    } finally {
      setIsProcessing(false);
      setActiveActionLabel(null);
    }
  };


  const handleQuickActionClick = async (action: QuickAction) => {
    if (isProcessing) return;

    setActiveActionLabel(action.label);
    if (action.directAction) {
      await executeDirectAction(action.label, action.directAction);
    } else {
      handleSendMessage(action.prompt);
    }
  };

  // Reset active action label when processing finishes
  React.useEffect(() => {
    if (!isProcessing) {
      setActiveActionLabel(null);
    }
  }, [isProcessing]);

  return {
    quickActions,
    handleQuickActionClick,
    executeDirectAction,
    activeActionLabel,
  };
};
